import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Twitter, Instagram, MapPin } from 'lucide-react';

const FooterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
    {children}
  </div>
);

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link 
    to={to}
    className="text-gray-400 hover:text-purple-400 transition-colors duration-200 block mb-2"
  >
    {children}
  </Link>
);

export default function Footer() {
  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand Section */}
          <div>
            <span className="text-2xl font-bold text-white">DiversiCoin</span>
            <p className="text-gray-400 mt-4">
              Making cryptocurrency accessible to everyone through our network of secure BTMs.
            </p>
            <div className="flex space-x-4 mt-6">
              <a 
                href="https://twitter.com/DiversiCoin" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-purple-400"
                aria-label="Follow us on Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/DiversiCoin" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-purple-400"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <FooterSection title="Quick Links">
            <nav className="space-y-2">
              <FooterLink to="/locations">Find BTMs</FooterLink>
              <FooterLink to="/host">Host a BTM</FooterLink>
              <FooterLink to="/bitcoin-basics">Bitcoin Basics</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
            </nav>
          </FooterSection>

          {/* Contact & Legal */}
          <FooterSection title="Support">
            <div className="space-y-4">
              <a
                href="mailto:support@diversicoin.cash"
                className="flex items-center space-x-2 text-gray-400 hover:text-purple-400 transition-colors duration-200"
              >
                <Mail className="w-5 h-5" />
                <span>support@diversicoin.cash</span>
              </a>
              <a
                href="https://maps.google.com/?q=5900+Balcones+Dr+STE+100,+Austin,+TX+78731"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-400 hover:text-purple-400 transition-colors duration-200"
              >
                <MapPin className="w-5 h-5" />
                <span>5900 Balcones Dr STE 100, Austin, TX 78731</span>
              </a>
              <div className="pt-4 space-y-2">
                <FooterLink to="/privacy-policy" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Privacy Policy</FooterLink>
                <FooterLink to="/terms-of-service" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Terms of Service</FooterLink>
                <FooterLink to="/cookie-policy" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Cookie Policy</FooterLink>
              </div>
            </div>
          </FooterSection>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
          <p>
            &copy; {new Date().getFullYear()} DiversiCoin. All rights reserved.<br />
            <span className="text-sm">
              We collect and use data in accordance with FinCEN regulations and for marketing purposes. 
              View our <Link to="/privacy-policy" className="text-purple-400 hover:text-purple-300" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Privacy Policy</Link> for details.
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}